// ./components/Customers/CustomersTable.js
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table, Tooltip, Icon, Badge, Dropdown, Menu, Button } from 'antd';

const tableStyles = `
@keyframes ct-float {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-4px); }
}
@keyframes ct-beat {
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.22); }
}
@keyframes ct-shake {
  0%, 100% { transform: rotate(0deg); }
  20%       { transform: rotate(-14deg); }
  40%       { transform: rotate(14deg); }
  60%       { transform: rotate(-8deg); }
  80%       { transform: rotate(8deg); }
}
@keyframes ct-bounce {
  0%, 100% { transform: translateY(0) scaleY(1); }
  40%       { transform: translateY(-5px) scaleY(1.05); }
  70%       { transform: translateY(2px) scaleY(0.96); }
}
@keyframes ct-glow {
  0%, 100% { box-shadow: 0 0 0 0 var(--gc, rgba(24,144,255,0.35)); }
  50%       { box-shadow: 0 0 0 8px rgba(0,0,0,0); }
}
@keyframes ct-spin   { 0% { transform: rotate(0deg); }   100% { transform: rotate(180deg); } }

.ct-float  { animation: ct-float  3s   ease-in-out infinite; }
.ct-beat   { animation: ct-beat   1.8s ease-in-out infinite; }
.ct-shake  { animation: ct-shake  2.6s ease-in-out infinite; }
.ct-bounce { animation: ct-bounce 2.2s ease-in-out infinite; }
.ct-glow   { animation: ct-glow   2.4s ease-in-out infinite; }

.ct-row-stripe td { background: #f7f9ff !important; }

.ant-table-thead > tr > th .ant-table-filter-icon,
.ant-table-thead > tr > th .anticon-filter {
  color: #1890ff !important; opacity: 1 !important; visibility: visible !important;
}
.ant-table-filter-dropdown {
  border-radius: 10px !important;
  box-shadow: 0 8px 28px rgba(24,144,255,0.15), 0 2px 8px rgba(0,0,0,0.08) !important;
  border: 1px solid #e6f0ff !important; overflow: hidden !important;
}
.ant-table-filter-dropdown .ant-dropdown-menu-item:hover { background: #e6f7ff !important; }
.ant-table-filter-dropdown-link { color: #1890ff !important; font-weight: 600 !important; }

.ct-status {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px; border-radius: 20px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.5px; white-space: nowrap;
}
.ct-status-pending     { background: #fffbe6; color: #d46b08; border: 1px solid #ffe58f; }
.ct-status-rejected    { background: #fff1f0; color: #cf1322; border: 1px solid #ffa39e; }
.ct-status-recommended { background: #e6fffb; color: #08979c; border: 1px solid #87e8de; }
.ct-status-approved    { background: #f6ffed; color: #389e0d; border: 1px solid #b7eb8f; }
.ct-status-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.ct-status-pending     .ct-status-dot { background: #d46b08; }
.ct-status-rejected    .ct-status-dot { background: #cf1322; }
.ct-status-recommended .ct-status-dot { background: #08979c; }
.ct-status-approved    .ct-status-dot { background: #389e0d; box-shadow: 0 0 5px #52c41a; }

.ct-settings-btn {
  width: 28px !important; height: 28px !important; min-width: 28px !important;
  padding: 0 !important; border-radius: 8px !important;
  display: inline-flex !important; align-items: center !important; justify-content: center !important;
  font-size: 14px !important; border: 1.5px solid #d6e8ff !important;
  background: #e6f7ff !important; color: #1890ff !important;
  transition: background 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s !important;
}
.ct-settings-btn.open {
  background: #1890ff !important; color: #fff !important;
  border-color: #1890ff !important; box-shadow: 0 4px 12px rgba(24,144,255,0.35) !important;
}
.ct-settings-btn.open .anticon-setting {
  animation: ct-spin 0.45s cubic-bezier(.22,1,.36,1) forwards !important;
}

.ct-action-menu.ant-dropdown-menu {
  border-radius: 10px !important; border: 1px solid #e6f0ff !important;
  box-shadow: 0 8px 28px rgba(24,144,255,0.15), 0 2px 8px rgba(0,0,0,0.08) !important;
  padding: 5px !important; min-width: 145px !important;
}
.ct-action-menu .ant-dropdown-menu-item {
  border-radius: 7px !important; padding: 8px 12px !important;
  font-size: 12px !important; font-weight: 600 !important; white-space: nowrap !important;
  transition: background 0.15s, transform 0.12s !important;
}
.ct-action-menu .ant-dropdown-menu-item:hover { transform: translateX(3px) !important; }
.ct-action-menu .ant-dropdown-menu-item-divider { margin: 4px 0 !important; background: #f0f5ff !important; }
.ct-menu-edit.ant-dropdown-menu-item   { color: #13c2c2 !important; }
.ct-menu-edit.ant-dropdown-menu-item:hover   { background: #e6fffb !important; }
.ct-menu-delete.ant-dropdown-menu-item { color: #ff4d4f !important; }
.ct-menu-delete.ant-dropdown-menu-item:hover { background: #fff1f0 !important; }
.ct-menu-icon {
  width: 18px; height: 18px; border-radius: 5px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 11px; flex-shrink: 0; margin-right: 8px; vertical-align: middle;
}
.ct-menu-edit   .ct-menu-icon { background: #e6fffb; }
.ct-menu-delete .ct-menu-icon { background: #fff1f0; }
`;

const STATUS_MAP = {
  'PENDING APPROVAL': { cls: 'ct-status-pending',     label: 'PENDING APPROVAL' },
  'REJECTED':         { cls: 'ct-status-rejected',    label: 'REJECTED' },
  'RECOMMENDED':      { cls: 'ct-status-recommended', label: 'RECOMMENDED' },
  'APPROVED':         { cls: 'ct-status-approved',    label: 'APPROVED' },
};

// ActionCell component
const ActionCell = React.memo(({ record, openModal, deleteCustomer, setLoading }) => {
  const [open, setOpen] = React.useState(false);
  const menu = (
    <Menu className="ct-action-menu">
      <Menu.Item key="edit" className="ct-menu-edit" onClick={() => openModal(record)}>
        <span className="ct-menu-icon"><Icon type="edit" /></span>Edit Record
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="delete" className="ct-menu-delete" onClick={() => {
        if (window.confirm('Are you sure you want to delete this customer?')) {
          deleteCustomer(record.id, setLoading);
        }
      }}>
        <span className="ct-menu-icon"><Icon type="delete" /></span>Delete
      </Menu.Item>
    </Menu>
  );
  return (
    <Dropdown overlay={menu} trigger={['hover']} placement="bottomLeft"
      getPopupContainer={() => document.body} onVisibleChange={v => setOpen(v)}>
      <Button className={`ct-settings-btn${open ? ' open' : ''}`} size="small" icon="setting" />
    </Dropdown>
  );
});

ActionCell.displayName = 'ActionCell';

const CustomersTable = ({ data, openModal, deleteCustomer, setLoading }) => {
  // Columns
  const columns = useMemo(() => [
    {
      key: 'actions',
      title: 'ACTIONS',
      width: 80,
      fixed: 'left',
      align: 'center',
      render: (record) => (
        <ActionCell 
          record={record} 
          openModal={openModal}
          deleteCustomer={deleteCustomer} 
          setLoading={setLoading} 
        />
      ),
    },
    {
      align: 'center',
      key: 'isApproved',
      title: 'STATUS',
      render: (record) => {
        let status = null;
        const cfg = STATUS_MAP[record.isApproved];
        if (cfg) {
          status = (
            <span className={`ct-status ${cfg.cls}`}>
              <span className="ct-status-dot" />{cfg.label}
            </span>
          );
        } else {
          status = <span>{record.isApproved}</span>;
        }
        return (
          <>
            {status}
            {record.comment && (
              <Tooltip title={record.comment}>
                <Icon style={{ marginLeft: 5 }} type="info-circle" theme="twoTone" />
              </Tooltip>
            )}
          </>
        );
      },
      filters: [
        { text: 'PENDING APPROVAL', value: 'PENDING APPROVAL' },
        { text: 'REJECTED',         value: 'REJECTED' },
        { text: 'RECOMMENDED',      value: 'RECOMMENDED' },
        { text: 'APPROVED',         value: 'APPROVED' },
      ],
      onFilter: (value, record) => record.isApproved && record.isApproved.indexOf(value) === 0,
      sorter: (a, b) => {
        const priority = {
          'APPROVED': 4,
          'RECOMMENDED': 3,
          'PENDING APPROVAL': 2,
          'REJECTED': 1
        };
        const priorityA = priority[a.isApproved] || 0;
        const priorityB = priority[b.isApproved] || 0;
        return priorityB - priorityA;
      },
      sortDirections: ['descend', 'ascend'],
      defaultSortOrder: 'descend',
      width: 160,
    },
    { 
      key: 'bi_name',    
      dataIndex: 'bi_name',    
      title: 'COMPANY NAME', 
      align: 'center',
      sorter: (a, b) => (a.bi_name || '').localeCompare(b.bi_name || ''),
      sortDirections: ['descend', 'ascend'],
    },
    { 
      key: 'bi_address', 
      dataIndex: 'bi_address', 
      title: 'ADDRESS',      
      align: 'center', 
      width: 280,
      sorter: (a, b) => (a.bi_address || '').localeCompare(b.bi_address || ''),
      sortDirections: ['descend', 'ascend'],
    },
    { 
      key: 'bi_tel',     
      dataIndex: 'bi_tel',     
      title: 'TEL. NO.',     
      align: 'center',
      sorter: (a, b) => (a.bi_tel || '').localeCompare(b.bi_tel || ''),
      sortDirections: ['descend', 'ascend'],
    },
    { 
      key: 'bi_email',   
      dataIndex: 'bi_email',   
      title: 'EMAIL',        
      align: 'center',
      sorter: (a, b) => (a.bi_email || '').localeCompare(b.bi_email || ''),
      sortDirections: ['descend', 'ascend'],
    },
  ], [openModal, deleteCustomer, setLoading]);

  return (
    <>
      <style>{tableStyles}</style>
      <Table
        columns={columns}
        dataSource={data}
        scroll={{ x: 'max-content' }}
        rowKey="id"
        size="middle"
        bordered
        rowClassName={(_, idx) => idx % 2 === 0 ? '' : 'ct-row-stripe'}
        pagination={{
          pageSizeOptions: ['10', '25', '50', '100'],
          showSizeChanger: true,
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} customers`,
        }}
      />
    </>
  );
};

CustomersTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  openModal: PropTypes.func.isRequired,
  deleteCustomer: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
};

export default CustomersTable;